import { AnimatedSection } from "@/hooks/useScrollAnimation";
import { ShieldCheck, Sparkles, Heart } from "lucide-react";
import parentChildImg from "@/assets/home-parent-child.jpg";
import babysitterKidsImg from "@/assets/home-babysitter-kids.jpg";
import trustHandsImg from "@/assets/home-trust-hands.jpg";

const items = [
  {
    src: parentChildImg,
    alt: "Parent lisant un livre avec son enfant à la maison",
    title: "Des moments en famille",
    desc: "Reprenez du temps pour vous, l'esprit léger.",
    icon: Heart,
    iconColor: "text-secondary",
    glow: "glow-pink",
  },
  {
    src: babysitterKidsImg,
    alt: "Baby-sitter souriante jouant avec deux enfants",
    title: "Des intervenants bienveillants",
    desc: "Des profils humains, attentionnés et qualifiés.",
    icon: Sparkles,
    iconColor: "text-primary",
    glow: "glow-violet",
  },
  {
    src: trustHandsImg,
    alt: "Main d'adulte tenant la main d'un jeune enfant, symbole de confiance",
    title: "La confiance avant tout",
    desc: "Identités vérifiées, paiement sécurisé, suivi 24/7.",
    icon: ShieldCheck,
    iconColor: "text-accent",
    glow: "glow-blue",
  },
];

const HomeImageGallery = () => {
  return (
    <section className="py-16 lg:py-20 relative overflow-hidden bg-section-violet">
      <div className="neon-orb neon-orb-violet w-[400px] h-[400px] -top-[120px] -left-[100px]" />
      <div className="neon-orb neon-orb-pink w-[350px] h-[350px] -bottom-[100px] -right-[80px]" />

      <div className="relative w-full px-4 md:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-12 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 card-glass-premium px-4 py-2 rounded-full text-xs font-semibold text-primary mb-5">
            <Heart className="w-3.5 h-3.5" />
            Une plateforme humaine
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground mb-3 tracking-tight">
            Des familles, des pros, <span className="text-gradient-neon">une vraie confiance</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Click&Gard, c'est avant tout des relations humaines, sécurisées et sereines.
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {items.map((item, i) => (
            <AnimatedSection key={item.title} delay={i * 150} animation="fade-up">
              <article
                className={`group relative overflow-hidden rounded-3xl card-glass-premium ${item.glow} h-full transition-transform duration-500 hover:-translate-y-1`}
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    width={1024}
                    height={1024}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  {/* Neon gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/20 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 mix-blend-overlay opacity-60 group-hover:opacity-90 transition-opacity duration-500" />

                  {/* Floating glass icon badge */}
                  <div className="absolute top-4 left-4">
                    <div className={`w-11 h-11 rounded-2xl card-glass-premium flex items-center justify-center backdrop-blur-xl ${item.glow}`}>
                      <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-6">
                  <h3 className="text-lg font-bold text-foreground mb-1.5 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                {/* Subtle inner border glow on hover */}
                <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-primary/0 group-hover:ring-primary/30 transition-all duration-500" />
              </article>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeImageGallery;
